import React, { useState, useEffect } from 'react';
import './EventCategoriesSetup.css';

const EventCategoriesSetup = () => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [formData, setFormData] = useState({
    id: '',
    eventTypeId: '5A9CCE66-C93B-42F7-AC76-996EDBFA6EF6',
    code: '',
    displayName: '',
    gender: 'Male',
    ageGroup: '',
    isDoubles: false,
    notes: ''
  });

  const [formErrors, setFormErrors] = useState({});

  // Load data from API
  useEffect(() => {
    loadCategories();
  }, []);

  // Handle search functionality
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredCategories(categories);
    } else {
      const filtered = categories.filter((category) => {
        const searchLower = searchQuery.toLowerCase();
        return (
          category.code?.toLowerCase().includes(searchLower) ||
          category.displayName?.toLowerCase().includes(searchLower) ||
          category.gender?.toLowerCase().includes(searchLower) ||
          category.ageGroup?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredCategories(filtered);
    }
  }, [searchQuery, categories]);

  const loadCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch('https://api.asterickx.com/api/EventCategories', {
        method: 'GET',
        headers: headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Loaded categories:', data);
      setCategories(data);
      setFilteredCategories(data);
    } catch (err) {
      setError('Failed to load categories: ' + err.message);
      console.error('Error loading categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    }

    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Display Name is required';
    }

    if (!formData.ageGroup.trim()) {
      newErrors.ageGroup = 'Age Group is required';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const openModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        id: category.id,
        eventTypeId: category.eventTypeId,
        code: category.code,
        displayName: category.displayName,
        gender: category.gender,
        ageGroup: category.ageGroup,
        isDoubles: category.isDoubles,
        notes: category.notes || ''
      });
    } else {
      setEditingCategory(null);
      setFormData({
        id: '',
        eventTypeId: '5A9CCE66-C93B-42F7-AC76-996EDBFA6EF6',
        code: '',
        displayName: '',
        gender: 'Male',
        ageGroup: '',
        isDoubles: false,
        notes: ''
      });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
    setFormData({
      id: '',
      eventTypeId: '5A9CCE66-C93B-42F7-AC76-996EDBFA6EF6',
      code: '',
      displayName: '',
      gender: 'Male',
      ageGroup: '',
      isDoubles: false,
      notes: ''
    });
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      if (editingCategory) {
        // Update existing category
        const response = await fetch(`https://api.asterickx.com/api/EventCategories/${formData.id}`, {
          method: 'PUT',
          headers: headers,
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        setCategories(prev =>
          prev.map(cat => cat.id === formData.id ? { ...formData, createdAt: cat.createdAt } : cat)
        );
        setFilteredCategories(prev =>
          prev.map(cat => cat.id === formData.id ? { ...formData, createdAt: cat.createdAt } : cat)
        );
      } else {
        // Create new category
        const newCategory = {
          ...formData,
          id: crypto.randomUUID(),
          createdAt: new Date().toISOString()
        };

        const response = await fetch('https://api.asterickx.com/api/EventCategories', {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(newCategory)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const savedCategory = await response.json();
        setCategories(prev => [...prev, savedCategory]);
        setFilteredCategories(prev => [...prev, savedCategory]);
      }

      closeModal();
    } catch (err) {
      setError('Failed to save category: ' + err.message);
      console.error('Error saving category:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`https://api.asterickx.com/api/EventCategories/${id}`, {
        method: 'DELETE',
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setCategories(prev => prev.filter(cat => cat.id !== id));
      setFilteredCategories(prev => prev.filter(cat => cat.id !== id));
    } catch (err) {
      setError('Failed to delete category: ' + err.message);
      console.error('Error deleting category:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get badge class for gender
  const getGenderBadgeClass = (gender) => {
    const genderLower = gender?.toLowerCase();
    if (genderLower === 'male') return 'badge-male';
    if (genderLower === 'female') return 'badge-female';
    return 'badge-mixed';
  };

  return (
    <div className="container mt-5" style={{ marginBottom: "10rem" }}>
      {/* Header Section */}
      <div className="header-section">
        <h2 className="page-title">Event Categories</h2>
        <div className="header-actions">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search categories..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <svg 
              className="search-icon" 
              xmlns="http://www.w3.org/2000/svg" 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
          </div>
          <button 
            className="btn-create-tournament"
            onClick={() => openModal()}
            disabled={loading}
          >
            <span className="btn-icon">+</span>
            Add New Category
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-bordered table-hover">
          <thead className="table-dark">
            <tr>
              <th>Code</th>
              <th>Display Name</th>
              <th>Gender</th>
              <th>Age Group</th>
              <th>Type</th>
              <th>Notes</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : filteredCategories.length > 0 ? (
              filteredCategories.map((category) => (
                <tr key={category.id}>
                  <td>
                    <span className="code-badge">{category.code}</span>
                  </td>
                  <td className="font-weight-medium">{category.displayName}</td>
                  <td>
                    <span className={`badge ${getGenderBadgeClass(category.gender)}`}>
                      {category.gender}
                    </span>
                  </td>
                  <td>{category.ageGroup}</td>
                  <td>
                    <span className={`badge ${category.isDoubles ? 'badge-doubles' : 'badge-singles'}`}>
                      {category.isDoubles ? 'Doubles' : 'Singles'}
                    </span>
                  </td>
                  <td className="notes-cell">{category.notes || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-action btn-edit"
                        onClick={() => openModal(category)}
                        disabled={loading}
                        title="Edit"
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path>
                          <path d="m15 5 4 4"></path>
                        </svg>
                      </button>
                      <button
                        className="btn-action btn-delete"
                        onClick={() => handleDelete(category.id)}
                        disabled={loading}
                        title="Delete"
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="m19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center">
                  {searchQuery ? "No categories found matching your search" : "No categories available"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal for Add/Edit */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCategory ? 'Edit Category' : 'Add New Category'}</h2>
              <button className="btn-close" onClick={closeModal}>&times;</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="code">Code *</label>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    className={formErrors.code ? 'input-error' : ''}
                    placeholder="e.g., BS_11"
                    disabled={loading}
                  />
                  {formErrors.code && <span className="error-message">{formErrors.code}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="displayName">Display Name *</label>
                  <input
                    type="text"
                    id="displayName"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleInputChange}
                    className={formErrors.displayName ? 'input-error' : ''}
                    placeholder="e.g., Boy's Singles"
                    disabled={loading}
                  />
                  {formErrors.displayName && <span className="error-message">{formErrors.displayName}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="gender">Gender *</label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Mixed">Mixed</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="ageGroup">Age Group *</label>
                  <input
                    type="text"
                    id="ageGroup"
                    name="ageGroup"
                    value={formData.ageGroup}
                    onChange={handleInputChange}
                    className={formErrors.ageGroup ? 'input-error' : ''}
                    placeholder="e.g., 11, U45"
                    disabled={loading}
                  />
                  {formErrors.ageGroup && <span className="error-message">{formErrors.ageGroup}</span>}
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="isDoubles"
                      checked={formData.isDoubles}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                    <span>Is Doubles</span>
                  </label>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="notes">Notes</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Optional notes..."
                    rows="3"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : (editingCategory ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventCategoriesSetup;